import { EntitiesRankingTableUIComponentCustomSettings, EntityChildrenListUIComponentCustomSettings } from '.';
import { EntityDetailsPageLayout, EntityDetailsCompsPlacement } from './EntityDetailsLayouts';


// This file is where we define all the layout templates the agent/developer can configure the entityClass to use. These are NOT all the
// possible layouts. The agent/developer can also use custom layout if none of the layout defined here support what they need to do.


/**
 *  -----------------------
 * |         area         |
 *  -----------------------
 */
const entityDetailsDefaultLayout: EntityDetailsPageLayout = {
    templateAreas: [
        ['area'],
    ],
    templateColumns: 'repeat(1, minmax(0, 1fr))',
};



/**
 *  ------------------------------------------
 * |                  top_area                |
 * |------------------------------------------
 * |  bottom_left_area  |  bottom_right_area  |
 *  ------------------------------------------
 */
const entityDetailsSingleAndDoubleColumnsLayout: EntityDetailsPageLayout = {
    templateAreas: [
        ['top_area', 'top_area'],
        ['bottom_left_area', 'bottom_right_area'],
    ],
    templateColumns: 'repeat(2, minmax(0, 1fr))',


    // On small screen, we will need to convert the bottom areas into 1 column. We will place bottom_left_area on top of bottom_right_area instead
    // of side by side
    smallScreen: {
        templateAreas: [
            ['top_area'],
            ['bottom_left_area'],
            ['bottom_right_area'],
        ],
        templateColumns: 'repeat(1, minmax(0, 1fr))',
    },
};



/**
 *  ------------------------------------
 * |  top_left_area  |  top_right_area  |
 *  ------------------------------------
 * |            bottom_area             |
 *  ------------------------------------
 */
const entityDetailsDoubleAndSingleColumnsLayout: EntityDetailsPageLayout = {
    templateAreas: [
        ['top_left_area', 'top_right_area'],
        ['bottom_area', 'bottom_area'],
    ],
    templateColumns: 'repeat(2, minmax(0, 1fr))',

    // On small screen, we will need to convert the top areas into 1 column. We will place top_left_area on top of top_right_area instead
    // of side by side
    smallScreen: {
        templateAreas: [
            ['top_left_area'],
            ['top_right_area'],
            ['bottom_area'],
        ],
        templateColumns: 'repeat(1, minmax(0, 1fr))',
    },
};


/**
 *  ------------------------------------------
 * |                  top_area                |
 * |------------------------------------------
 * |                    |                     |
 * |  middle_left_area  |  middle_right_area  |
 * |                    |                     |
 *  ------------------------------------------
 * |                bottom_area               |
 * |------------------------------------------
 */
const entityDetailsSingleDoubleSingleColumnsLayout: EntityDetailsPageLayout = {
    templateAreas: [
        ['top_area', 'top_area'],
        ['middle_left_area', 'middle_right_area'],
        ['bottom_area', 'bottom_area'],
    ],
    templateColumns: 'repeat(2, minmax(0, 1fr))',


    // On small screen, we will need to convert the middle areas into 1 column. We will place middle_left_area on top of middle_right_area instead
    // of side by side
    smallScreen: {
        templateAreas: [
            ['top_area'],
            ['middle_left_area'],
            ['middle_right_area'],
            ['bottom_area'],
        ],
        templateColumns: 'repeat(1, minmax(0, 1fr))',
    },
};



/**
 *  ------------------------------------------
 * |   top_left_area    |   top_right_area    |
 *  ------------------------------------------
 * |                                          |
 * |               middle_area                |
 * |                                          |
 *  ------------------------------------------
 * |  bottom_left_area  |  bottom_right_area  |
 *  ------------------------------------------
 */
const entityDetailsDoubleSingleDoubleColumnsLayout: EntityDetailsPageLayout = {
    templateAreas: [
        ['top_left_area', 'top_right_area'],
        ['middle_area', 'middle_area'],
        ['bottom_left_area', 'bottom_right_area'],
    ],
    templateColumns: 'repeat(2, minmax(0, 1fr))',

    // On small screen, we will need to convert the top and bottom areas into 1 column. We will place top_left_area on top of top_right_area instead
    // of side by side. And also bottom_left_area on top of bottom_right_area.
    smallScreen: {
        templateAreas: [
            ['top_left_area'],
            ['top_right_area'],
            ['middle_area'],
            ['bottom_left_area'],
            ['bottom_right_area'],
        ],
        templateColumns: 'repeat(1, minmax(0, 1fr))',
    },
};


/**
 *  -------------------------------
 * |             |                 |
 * |  left_area  |   right_area    |
 * |             |                 |
 *  -------------------------------
 */
const entityDetailsDoubleColumnsLayout: EntityDetailsPageLayout = {
    templateAreas: [
        ['left_area', 'right_area'],
    ],
    templateColumns: 'repeat(2, minmax(0, 1fr))',

    // Even though this is 2 columns layout, on small screen, we will have to turn this to 1 columns. It will still have 2 areas.
    // But the 'right_area' will be placed below 'left_area' instead of on the right
    smallScreen: {
        templateAreas: [
            ['left_area'],
            ['right_area'],
        ],
        templateColumns: 'repeat(1, minmax(0, 1fr))',
    },
};



/**
 * Create a predefined map of layoutName => EntityDetailsPageLayout. In entity class display settings, we will refer to these layouts by
 * these layoutNames. For example, if we want an entity class 'Fridge' to use the 2 columns layout, we just set it's layout attribute
 * in display settings to 'double'. The layout container will lookup the layout settings by this name and use it to layout the
 * components.
 *
 *
 */
export const ALL_PREDEFINED_ENTITY_DETAILS_LAYOUTS: {
    readonly [layoutName: string]: EntityDetailsPageLayout | undefined;
} = {
    default             : entityDetailsDefaultLayout,
    single_double       : entityDetailsSingleAndDoubleColumnsLayout,
    double_single       : entityDetailsDoubleAndSingleColumnsLayout,
    single_double_single: entityDetailsSingleDoubleSingleColumnsLayout,
    double_single_double: entityDetailsDoubleSingleDoubleColumnsLayout,
    double              : entityDetailsDoubleColumnsLayout,
};


// Default layout and components placement to use when an entityClass does not have custom settings or have invalid settings
export const DEFAULT_ENTITY_DETAILS_LAYOUT: EntityDetailsPageLayout = entityDetailsDefaultLayout;
export const DEFAULT_ENTITY_DETAILS_COMPONENT_PLACEMENT: EntityDetailsCompsPlacement = {
    area: [{
        componentType : 'Entity_Children_List_Comp',
        customSettings: {
            gridSizes          : { xs: 12, md: 6, lg: 4 },
            childrenUIComponent: {
                componentType: 'Entity_KPI_Preview_Comp',
            },
        } as EntityChildrenListUIComponentCustomSettings,
    }, {
        componentType: 'Entity_KPI_Metrics_Comp',
    }, {
        componentType : 'Entity_Ranking_Table_Comp',
        customSettings: {
            maxItems: 10,
        } as EntitiesRankingTableUIComponentCustomSettings,
    }],
};
